import { mapGetters } from 'vuex'

export const bestOfferValidations = {
  computed: {
    ...mapGetters(['campaign']),
    hasOfferSales() {
      if (this.campaign?.offer_sales?.length > 0) {
        return true
      }

      const offerSchedules = this.campaign?.offer_schedules || []
      return offerSchedules.some((schedule) => schedule.offer_sales.length > 0)
    },
  },
  methods: {
    hasMoreThanOneBestOffer(message) {
      const regex = /\[\d+\s+melhor oferta\]/gi
      const matches = [...message.matchAll(regex)]
      return matches.length > 1
    },
    hasInvalidBestOffer(message) {
      const regexInvalid = /\[\s*(?:([^\d\s]|0|1[1-9]|\d{3,})\s+)?melhor oferta\s*\]/i
      return regexInvalid.test(message)
    },
    extractBestOfferQuantity(message) {
      const regexBestOffer = /\[(\d+)\s+melhor oferta\]/gi
      const matches = [...message.matchAll(regexBestOffer)]

      if (matches.length === 0) {
        return null
      }

      return parseInt(matches[0][1], 10)
    },
    extractProductsFromOfferSchedules() {
      let products = []
      if (this.campaign?.offer_sales?.length > 0) {
        products.push(...this.campaign?.offer_sales)
      } else if (this.campaign?.offer_schedules?.length > 0) {
        this.campaign?.offer_schedules?.forEach((schedule) => {
          schedule?.offer_sales?.forEach((offer) => {
            if (offer?.offer_sales?.length > 0) {
              products.push(
                ...offer.offer_sales.map((product) => ({
                  ...product,
                  price_1: offer?.price_1 || null,
                  price_2: offer?.price_2 || null,
                }))
              )
            } else {
              products.push(offer)
            }
          })
        })
      }

      return products
    },
    hasEnoughProducts(message) {
      if (!this.hasOfferSales) {
        return true
      }

      const bestOfferQuantity = this.extractBestOfferQuantity(message)
      if (bestOfferQuantity === null) {
        return true
      }

      const products = this.extractProductsFromOfferSchedules()

      const hasEnough = products?.length >= bestOfferQuantity

      return hasEnough
    },
    isPricesCorrect(channel, field, offerType, message, isFormattingInsideBestOffer = false) {
      if (!this.hasOfferSales) {
        return true
      }

      if (isFormattingInsideBestOffer) {
        if (!message.includes('{preço 1}') && !message.includes('{preço 2}')) {
          return true
        }
      } else {
        const bestOfferQuantity = this.extractBestOfferQuantity(message)
        if (bestOfferQuantity === null) {
          return true
        }
      }

      const channelConfig = this.campaignVariablesConfiguration?.[channel]?.[field]?.[offerType]
      if (!channelConfig) {
        return true
      }

      const hasPrice1Subvariable = channelConfig.repeatable_text?.includes('{preço 1}') || channelConfig.validity_text?.includes('{preço 1}')
      const hasPrice2Subvariable = channelConfig.repeatable_text?.includes('{preço 2}') || channelConfig.validity_text?.includes('{preço 2}')
      if (!hasPrice1Subvariable && !hasPrice2Subvariable) {
        return true
      }

      const products = this.extractProductsFromOfferSchedules()
      if (products?.length === 0) {
        return true
      }

      const priceValidation = (product) => {
        return product.price_1 !== null && product.price_1 !== undefined && product.price_1 !== '' && product.price_1 !== 0
      }

      return products?.every((product) => {
        const hasPrice1 = hasPrice1Subvariable ? priceValidation(product) : true
        const hasPrice2 = hasPrice2Subvariable ? priceValidation(product) : true
        return hasPrice1 && hasPrice2
      })
    },
    extractProductsFromScheduledOffers() {
      let products = []
      if (this.campaign?.offer_schedules?.length > 0) {
        this.campaign.offer_schedules.forEach((schedule) => {
          const offerTypes = [
            'cashback_absolute_offer_sales',
            'cashback_percentage_offer_sales',
            'combo_offer_sales',
            'next_buy_cashback_offer_sales',
            'offer_sales',
            'percentage_offer_sales',
            'points_offer_sales',
            'takepay_offer_sales',
          ]

          offerTypes.forEach((offerType) => {
            let groupedProducts = []
            if (schedule[offerType]?.length) {
              schedule[offerType]?.forEach((offer) => {
                if (offer?.__typename.includes('Group') || offer.group_id) {
                  if (
                    offer.group_rules_exhibition === 'PUBLIC' &&
                    offer.group_image_url &&
                    !groupedProducts.find((p) => (p.group_id || p._id) === (offer.group_id || offer._id))
                  ) {
                    groupedProducts.push(offer)
                  } else if (offer.group_rules_exhibition !== 'PUBLIC') {
                    if (offer[offerType]?.length) {
                      offer[offerType]?.forEach((product) => {
                        if (this.getProductImageUrl(product) && !groupedProducts.find((p) => p.sku === product.sku)) {
                          groupedProducts.push(product)
                        }
                      })
                    } else if (this.getProductImageUrl(offer) && !groupedProducts.find((p) => p.sku === offer.sku)) {
                      groupedProducts.push(offer)
                    }
                  }
                } else if (offer?.[offerType]?.length) {
                  offer[offerType].forEach((product) => {
                    if (this.getProductImageUrl(product) && !groupedProducts.find((p) => p.product._id === product.product._id)) {
                      groupedProducts.push(product)
                    }
                  })
                } else if (this.getProductImageUrl(offer)) {
                  groupedProducts.push(offer)
                }
              })

              if (!groupedProducts.length) {
                for (const offer of schedule[offerType]) {
                  if (
                    offer?.__typename.includes('Group') &&
                    !groupedProducts.find((product) => (product.group_id || product._id) === (offer.group_id || offer._id))
                  ) {
                    if (offer?.group_rules_exhibition === 'PUBLIC' && offer.group_image_url) {
                      groupedProducts.push(offer)
                    } else if (offer?.group_rules_exhibition === 'PRIVATE') {
                      offer[offerType].forEach((product) => {
                        if (this.getProductImageUrl(product)) {
                          let productWithGroupInfo = {
                            ...product,
                            group_id: offer.group_id,
                          }
                          if (offerType === 'offer_sales') {
                            productWithGroupInfo = {
                              ...productWithGroupInfo,
                              price_1: offer.price_1,
                              price_2: offer.price_2,
                            }
                          }
                          groupedProducts.push(productWithGroupInfo)
                        }
                      })
                    }
                  }
                }
              }
            }
            products.push(...groupedProducts)
          })
        })
      }
      return products
    },
    getAllProducts() {
      let products = []

      if (this.campaign?.offer_sales?.length > 0) {
        this.campaign?.offer_sales?.forEach((product) => {
          if (
            product.isGroup &&
            product.group_rules_exhibition === 'PUBLIC' &&
            product.group_image_url &&
            !products.find((p) => p.groupInfo?._id === product.groupInfo?._id)
          ) {
            if (product.price_1 && product.price_2 && product.active) {
              products.push(product)
            }
          } else if (!product.isGroup || product.group_rules_exhibition === 'PRIVATE') {
            const hasImage = this.getProductImageUrl(product) !== null

            if (product.price_1 && product.price_2 && hasImage && product.active) {
              const productWithGroupInfo = {
                ...product,
                group_rules_exhibition: product.group_rules_exhibition,
                group_name: product.group_name,
                groupInfo: product.groupInfo,
                group_image_url: product.group_image_url,
                group_id: product.group_id,
              }
              products.push(productWithGroupInfo)
            }
          }
        })
      }

      const scheduledProducts = this.extractProductsFromScheduledOffers()
      products.push(...scheduledProducts)
      return products
    },
    validateAllProductsImages() {
      const products = this.getAllProducts()
      const activeProducts = products.filter((p) => {
        return p.active || p.group_rules_exhibition === 'PUBLIC'
      })
      return activeProducts.length
    },
    getProductImageUrl(product) {
      return product?.image || product?.image_url || product?.product?.image || product?.product?.image_url || null
    },
  },
}
